/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { isNil } from 'lodash'
import React, { useEffect, useState } from 'react'
import { t } from 'i18next'
import { DeletePathwayModal } from '../../components/DeletePathwayModal'
import { usePathwayContextProvider } from '../../components/PathwayContextProvider'
import { StopPathwayModal } from '../../components/StopPathwayModal'
import { useAdHocTracks } from '../../hooks/useAdHocTracks'
import { usePathway } from '../../hooks/usePathway'
import { usePathwayContext } from '../../hooks/usePathwayContext'
import { StartTrackModal } from './StartTrackModal'
import { useGetHostedPagesLink } from '../../hooks/useGetHostedPagesLink'
import { HostedPagesQRButton } from './HostedPagesQRButton'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { useRetoolConfig } from '../../hooks/useRetoolConfig'
import { Icon, Tab, Button, Dropdown } from '@awell-health/design-system'
import { navigate, useLocation } from '@reach/router'

interface PathwayHeaderProps {
  loading: boolean
}

export const PathwayHeader = ({ loading }: PathwayHeaderProps): JSX.Element => {
  const { pathname } = useLocation()
  const { retoolConfig } = useRetoolConfig()
  // TODO add back dashboard tab
  const { isAuthorized: canAccessPatientDashboard } = useStytchIsAuthorized('worklist', 'patient_dashboard')

  const { pathwayId } = usePathwayContext()
  const { pathway } = usePathway(pathwayId)

  const [selectedTab, setSelectedTab] = useState('activity-feed')

  const isCurrentTabPath = (tabName: string): boolean => pathname.includes(tabName)

  useEffect(() => {
    console.log('pathname', pathname)
    if (isCurrentTabPath('activity-feed')) {
      setSelectedTab('activity-feed')
    } else if (isCurrentTabPath('timeline')) {
      setSelectedTab('timeline')
    } else if (isCurrentTabPath('logs')) {
      setSelectedTab('logs')
    } else if (isCurrentTabPath('dashboard')) {
      setSelectedTab('dashboard')
    }
  }, [])

  // check dashboard also exists in the retool metadata
  const app = retoolConfig.worklists.find(app => app.careflowId === pathway?.pathway_definition_id)
  const dashboardExists = !isNil(app)

  const [startTrackModalOpen, setStartTrackModalOpen] = React.useState<boolean>(false)
  const {
    pendingActivities,
    sidePanelOpen,
    onSelectActivity,
    onSelectElement,
    setSidePanelOpen,
    activities,
    pathwayIsActive,
    patientId
  } = usePathwayContextProvider()

  const { url: hostedPagesUrlForPatient } = useGetHostedPagesLink({
    pathway_id: pathwayId!,
    stakeholder_id: patientId
  })

  const { handleStartTrack, adHocTracks, adHocTracksLoading } = useAdHocTracks({
    pathway_id: pathwayId!
  })
  const [openModalToStopPathway, setOpenModalToStopPathway] = useState<boolean>(false)
  const [openModalToDeletePathway, setOpenModalToDeletePathway] = useState<boolean>(false)

  const onShowPendingActivities = (): void => {
    onSelectActivity(undefined)
    onSelectElement(undefined)
    setSidePanelOpen(true)
  }

  const getPendingActivitiesTitle = (): string => {
    if (loading) {
      return t('loading')
    }
    if (pendingActivities.length === 0) {
      return t('no_pending_activities')
    }
    return t('pending_activity', { count: pendingActivities.length })
  }

  const handleCloseStartTrackModal = (): void => {
    setStartTrackModalOpen(false)
  }

  const handleModalClose = (): void => {
    setOpenModalToDeletePathway(false)
    setOpenModalToStopPathway(false)
  }

  const pendingActivitiesButtonDisabled = sidePanelOpen || pendingActivities.length === 0 || loading

  const addTrackButtonDisabled =
    adHocTracksLoading || adHocTracks?.length === 0 || !pathwayIsActive || activities.length === 0

  // only an active pathway can be stopped
  const stopPathwayButtonDisabled = !pathwayIsActive

  // pathway can be deleted at any time
  const deletePathwayButtonDisabled = false

  const openDeleteConfirmationModal = (): void => {
    setOpenModalToDeletePathway(true)
  }

  const openStopConfirmationModal = (): void => {
    setOpenModalToStopPathway(true)
  }

  const tabs = [
    {
      id: 'activity-feed',
      label: (
        <div className="flex items-center gap-2">
          <Icon color={selectedTab === 'activity-feed' ? 'blue' : ''} icon="RiAlignJustify" size={16} />
          Activity Feed
        </div>
      ),
      onClick: () => {
        setSelectedTab('activity-feed')
        navigate(`/pathway/${pathwayId}/activity-feed`)
      }
    },
    {
      id: 'timeline',
      label: (
        <div className="flex items-center gap-2">
          <Icon color={selectedTab === 'timeline' ? 'blue' : ''} icon="RiTimeLine" size={16} />
          Timeline
        </div>
      ),
      onClick: () => {
        setSelectedTab('timeline')
        navigate(`/pathway/${pathwayId}/timeline`)
      }
    },
    {
      id: 'logs',
      label: (
        <div className="flex items-center gap-2">
          <Icon color={selectedTab === 'logs' ? 'blue' : ''} icon="RiTimelineView" size={16} />
          Logs
        </div>
      ),
      onClick: () => {
        setSelectedTab('logs')
        navigate(`/pathway/${pathwayId}/logs/audit-logs`)
      }
    }
  ]

  if (canAccessPatientDashboard && dashboardExists) {
    tabs.push({
      id: 'dashboard',
      label: (
        <div className="flex items-center gap-2">
          <Icon color={selectedTab === 'dashboard' ? 'blue' : ''} icon="RiLineChartLine" size={16} />
          Dashboard
        </div>
      ),
      onClick: () => {
        setSelectedTab('dashboard')
        navigate(`/pathway/${pathwayId}/dashboard`)
      }
    })
  }

  const dropdownItems = [
    {
      label: (
        <>
          <Icon icon="RiAddFill" size={16} /> {t('add_track')}
        </>
      ),
      disabled: addTrackButtonDisabled,
      onClick: () => {
        setStartTrackModalOpen(true)
      }
    },
    {
      label: (
        <>
          <Icon icon="RiStopCircleFill" size={16} /> {t('stop_care_flow')}
        </>
      ),
      disabled: stopPathwayButtonDisabled,
      onClick: () => {
        openStopConfirmationModal()
      }
    },
    {
      label: (
        <>
          <Icon icon="RiDeleteBinLine" color="red" size={16} /> {t('delete_care_flow')}
        </>
      ),
      disabled: deletePathwayButtonDisabled,
      onClick: () => {
        openDeleteConfirmationModal()
      }
    }
  ]

  return (
    <div className="flex justify-between items-center mx-4">
      <div>
        <Tab fullWidth={true} items={tabs} selected={selectedTab} size="md" variant="bordered" />
      </div>
      <div className="flex items-center gap-2">
        {pathway && (
          <HostedPagesQRButton hostedPagesUrlForPatient={hostedPagesUrlForPatient} pathwayStatus={pathway?.status} />
        )}
        <Dropdown
          buttonClassNames="text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300 active:bg-blue-100 active:border-blue-300 outline-blue-500"
          buttonLabel={t('manage')}
          items={dropdownItems}
          placement="left"
        />
        <Button
          onClick={onShowPendingActivities}
          disabled={pendingActivitiesButtonDisabled}
          variant="secondaryBlue"
          size={null}
        >
          <Icon color={pendingActivitiesButtonDisabled ? 'white' : 'blue'} icon="RiArrowLeftSLine" size={16} />
          {getPendingActivitiesTitle()}
        </Button>
        {startTrackModalOpen && (
          <StartTrackModal
            onClose={handleCloseStartTrackModal}
            tracks={adHocTracks}
            onStartTrack={track_definition_id => {
              void handleStartTrack(track_definition_id)
            }}
          />
        )}
        {openModalToDeletePathway && (
          <DeletePathwayModal
            onClose={handleModalClose}
            redirectToUri={!isNil(pathway?.patient.id) ? `/patients/${pathway.patient.id}/pathways` : `/patients`}
            patient_id={pathway?.patient.id}
            careflow_id={pathwayId}
            patientName={pathway?.patient.profile?.name ?? undefined}
            careflowName={pathway?.title}
          />
        )}
        {openModalToStopPathway && (
          <StopPathwayModal
            onClose={handleModalClose}
            careflow_id={pathwayId}
            patientName={pathway?.patient.profile?.name ?? undefined}
            careflowName={pathway?.title}
          />
        )}
      </div>
    </div>
  )
}

PathwayHeader.displayName = 'PathwayHeader'
